import { Upload } from "lucide-react";
import { FlightDetailsMatchPlay, FlightPlayer, Hole, ScoreMatchPlay } from "../../models/scoreModel";

interface MatchPlayScoreTableProps {
  scores: ScoreMatchPlay[];
  holes: Hole[];
  players: FlightPlayer[];
}

const MatchPlayScoreTable: React.FC<MatchPlayScoreTableProps> = ({
  scores,
  holes,
  players,
}) => {
  /* DO NOT DELETE
  const playerTotalsOLD = players.map((player) => {
    const totals = scores.reduce(
      (acc, score) => {
        const playerScore = score.scores?.find(
          (s) => s.playerId === player.playerId
        );
        if (playerScore) {
          if (playerScore.isConceded) {
            // If player conceded, add losses against all other players
            acc.losses += (players.length - 1);
          } else if (score.scores?.some(s => s.isConceded)) {
            // If other players conceded, add wins for each concession
            const concededCount = score.scores.filter(
              s => s.isConceded && s.playerId !== player.playerId
            ).length;
            acc.wins += concededCount;
          }
          // Add the regular W/L/D counts
          acc.wins += playerScore.winCount;
          acc.losses += playerScore.loseCount;
          acc.ties += playerScore.drawCount;
        }
        return acc;
      },
      { wins: 0, losses: 0, ties: 0 }
    );
    return { playerId: player.playerId, ...totals };
  }); */

  // Get holes that have scores
  const scoredHoles = scores
    .filter((score) =>
      score.scores?.some((s) => s.grossScore > 0 || s.isConceded)
    )
    .map((score) => score.holeNumber)
    .sort((a, b) => a - b);

  return (
    <div>
      {/* Desktop view - table */}
      <div className="hidden md:block overflow-x-auto bg-white dark:bg-gray-800 rounded-lg">
        {scoredHoles.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-gray-600 dark:text-gray-300">
                  Hole
                </th>
                <th className="px-4 py-3 text-gray-600 dark:text-gray-300">
                  Par
                </th>
                <th className="px-4 py-3 text-gray-600 dark:text-gray-300">
                  SI
                </th>
                {players.map((player) => (
                  <th
                    key={player.playerId}
                    className="px-4 py-3 text-gray-600 dark:text-gray-300"
                  >
                    <div>{`${player.player.firstName} ${player.player.lastName}`}</div>
                    
                  </th>
                ))}
                <th className="px-4 py-3 text-gray-600 dark:text-gray-300">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {holes
                .filter((hole) => scoredHoles.includes(hole.holeNumber))
                .map((hole) => {
                  const score = scores.find(
                    (s) => s.holeNumber === hole.holeNumber
                  );
                  return (
                    <tr
                      key={hole.holeNumber}
                      className="border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                        {hole.holeNumber}
                      </td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                        {hole.par}
                      </td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                        {hole.strokeIndex}
                      </td>
                      {players.map((player) => {
                        const playerScore = score?.scores?.find(
                          (s) => s.playerId === player.playerId
                        );

                        return (
                          <td
                            key={player.playerId}
                            className="px-4 py-3 text-gray-900 dark:text-gray-100"
                          >
                            <div>
                              {playerScore?.isConceded
                                ? "CON"
                                : playerScore?.grossScore || "-"}
                            </div>
                            {/* <div className="text-xs text-gray-600 dark:text-gray-400">
                              {`W/L/S: ${playerScore?.winCount}/${playerScore?.loseCount}/${playerScore?.drawCount}`}
                            </div> */}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            score?.isUploaded
                              ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                              : "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100"
                          }`}
                        >
                          {score?.isUploaded ? "Uploaded" : "Pending"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        ) : (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            No scores recorded yet
          </div>
        )}
      </div>

      {/* Mobile view - cards */}
      <div className="md:hidden space-y-4">
        {scoredHoles.length > 0 ? (
          <>
            
            {/* Show only holes with scores */}
            {holes
              .filter((hole) => scoredHoles.includes(hole.holeNumber))
              .map((hole) => {
                const score = scores.find(
                  (s) => s.holeNumber === hole.holeNumber
                );
                return (
                  <div
                    key={hole.holeNumber}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"
                  >
                    <div className="flex justify-between items-center mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
                      <div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          Hole {hole.holeNumber}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Par {hole.par} | SI {hole.strokeIndex}
                        </div>
                      </div>
                      <div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            score?.isUploaded
                              ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                              : "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100"
                          }`}
                        >
                          {score?.isUploaded ? "Uploaded" : "Pending"}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {players.map((player) => {
                        return (
                          <div
                            key={player.playerId}
                            className="flex justify-between items-center py-1"
                          >
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {player.player.firstName} {player.player.lastName}
                            </div>
                            <div className="flex items-center gap-2">
                              {(() => {
                                const playerScore = score?.scores?.find(
                                  (s) => s.playerId === player.playerId
                                );
                                const wld = playerScore
                                  ? (() => {
                                      if (playerScore.isConceded) {
                                        // If this player conceded, they lose against all other players
                                        return `W/L/S: 0/${
                                          players.length - 1
                                        }/0`;
                                      } else if (
                                        score?.scores?.some((s) => s.isConceded)
                                      ) {
                                        return `W/L/S: ${playerScore.winCount}/${playerScore.loseCount}/${playerScore.drawCount}`;
                                      }
                                      return `W/L/S: ${playerScore.winCount}/${playerScore.loseCount}/${playerScore.drawCount}`;
                                    })()
                                  : "";
                                return (
                                  <>
                                    {wld && (
                                      <span className="text-xs text-gray-600 dark:text-gray-400">
                                        {wld}
                                      </span>
                                    )}
                                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                                      {playerScore?.isConceded
                                        ? "CON"
                                        : playerScore?.grossScore || "-"}
                                    </span>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
          </>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center text-gray-500 dark:text-gray-400">
            No scores recorded yet
          </div>
        )}
      </div>
    </div>
  );
};

interface MatchPlayScoreListProps {
  scores: ScoreMatchPlay[];
  holes: Hole[];
  players: FlightPlayer[];
  onUpload: () => void;
  currentFlight?: FlightDetailsMatchPlay;
}

export const MatchPlayScoreList: React.FC<MatchPlayScoreListProps> = ({
  scores,
  holes,
  players,
  onUpload,
  currentFlight
}) => {
    const filteredScores = currentFlight 
        ? scores.filter(score => 
                score.scores.every(s => 
                    currentFlight.players.some(p => p.playerId === s.playerId)
                )
            )
        : scores;
  const pendingScores = scores.filter((s) => !s.isUploaded);

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Score Summary
        </h3>
        {pendingScores.length > 0 && (
          <button
            onClick={onUpload}
            className="inline-flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors duration-200"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload ({pendingScores.length})
          </button>
        )}
      </div>

      <MatchPlayScoreTable scores={filteredScores} holes={holes} players={players} />
    </div>
  );
};
